import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/app/AppShell";
import { libApi, apiError } from "@/lib/api";
import { FAVORITES } from "@/constants/testIds";

export default function Favorites() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let active = true;
    libApi
      .favorites()
      .then((d) => active && setItems(d.favorites || []))
      .catch(() => active && setItems([]));
    return () => {
      active = false;
    };
  }, []);

  const remove = async (slug) => {
    const prev = items;
    setItems((list) => list.filter((f) => f.prompt_slug !== slug));
    try {
      await libApi.removeFavorite(slug);
      toast.success("Usunięto z ulubionych.");
    } catch (err) {
      setItems(prev);
      toast.error(apiError(err.response?.data?.detail, "Nie udało się usunąć."));
    }
  };

  return (
    <AppShell>
      <div data-testid={FAVORITES.root}>
        <header className="mb-8">
          <h1 className="font-display text-3xl font-semibold text-brand-text sm:text-4xl">Ulubione</h1>
          <p className="mt-2 text-brand-sec">Szablony Promptivo oznaczone sercem.</p>
        </header>

        {items === null ? (
          <div className="grid gap-5 sm:grid-cols-2">
            {[0, 1].map((i) => (
              <div key={i} className="h-32 animate-pulse rounded-card border border-brand-line bg-brand-surface" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div data-testid={FAVORITES.empty} className="flex flex-col items-start gap-4 rounded-panel border border-brand-line bg-brand-surface px-6 py-10">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-elevated text-brand-accent">
              <Heart size={22} strokeWidth={1.75} />
            </div>
            <div>
              <h2 className="font-display text-xl font-semibold text-brand-text">Nie masz jeszcze ulubionych promptów</h2>
              <p className="mt-2 max-w-lg text-brand-sec">
                Dodaj serce przy szablonie, do którego chcesz szybko wracać.
              </p>
            </div>
            <Link to="/biblioteka" className="inline-flex items-center gap-2 rounded-button bg-brand-accent px-5 py-3 text-sm font-semibold text-brand-on transition-transform hover:-translate-y-0.5">
              Przejdź do biblioteki <ArrowRight size={16} strokeWidth={2} />
            </Link>
          </div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2">
            {items.map((f) => (
              <article key={f.prompt_slug} data-testid={FAVORITES.card} className="flex flex-col rounded-card border border-brand-line bg-brand-surface p-5">
                <span className="text-xs font-medium uppercase tracking-wide text-brand-muted">
                  {f.package_name} · {f.category_slug}
                </span>
                <h3 className="mt-3 font-display text-lg font-semibold text-brand-text">{f.name}</h3>
                <p className="mt-1.5 flex-1 text-sm text-brand-sec">{f.short_benefit}</p>
                <div className="mt-5 flex items-center gap-3">
                  <Link
                    to={`/biblioteka/${f.package_slug}`}
                    data-testid={FAVORITES.openButton}
                    className="inline-flex items-center gap-2 rounded-field border border-brand-line px-4 py-2 text-sm font-medium text-brand-text transition-colors hover:border-brand-accent/50"
                  >
                    Otwórz w pakiecie
                  </Link>
                  <button
                    type="button"
                    data-testid={FAVORITES.removeButton}
                    onClick={() => remove(f.prompt_slug)}
                    className="text-sm font-medium text-brand-muted transition-colors hover:text-red-400"
                  >
                    Usuń z ulubionych
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
